import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { isCommunityFeatureEnabled, getEmbeddedAppUrl } from '../utils/featureHelpers';
import { getEmbeddedApp } from '../config/embeddedApps';

/**
 * EmbeddedAppPage — hosts an external webapp (Library, Housing, ...) inside the
 * host webapp via iframe, with a slim sticky header carrying the app's name and
 * a close button (X) to return.
 *
 * This is the generalisation of what used to be LibraryPage.js. Which app it
 * shows is decided entirely by the `appId` prop, resolved through
 * config/embeddedApps.js — so a second or third app costs a registry entry,
 * not a copy of this file.
 *
 * Why an iframe rather than a redirect:
 *   - Keeps the user in the host PWA so they don't lose their session, scroll,
 *     or service-worker state.
 *   - Avoids the "open in browser" UX disruption on installed PWAs.
 *
 * Failure modes handled:
 *   - The target never responds at all: we time out after 10s if `load` never
 *     fires and show an "Open in new tab" fallback.
 *   - The target refuses framing (`X-Frame-Options: DENY` or a CSP with
 *     `frame-ancestors 'none'`). This one CANNOT be detected from the parent.
 *     Measured in Chromium against a `frame-ancestors 'none'` target: `load`
 *     still fires (~700ms) on the browser's error page, no `error` event, no
 *     `securitypolicyviolation` on the parent (that report goes to the framed
 *     site, not to us), and `contentWindow` / `contentWindow.length` read
 *     identically to a healthy cross-origin frame. So the 10s timeout above
 *     does NOT catch it — the user would just see an empty pane.
 *
 *     Two consequences, both deliberate:
 *       a) The header carries a persistent "open in new tab" control, so a
 *          blocked embed is still a dead end the user can walk out of.
 *       b) Before registering a new app, check that it permits framing:
 *              curl -sSI <url> | grep -i 'x-frame-options\|content-security-policy'
 *          The target must allow this host, e.g.
 *              frame-ancestors 'self' https://dez-lectures.netlify.app;
 *   - The user wants to back out — the close button always returns home.
 */
const EmbeddedAppPage = ({ appId }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const app = getEmbeddedApp(appId);
  const appUrl = getEmbeddedAppUrl(config, appId);
  // An unregistered id can never be enabled, which routes us to the same
  // "not enabled" screen rather than iframing `undefined`.
  const enabled = !!app && isCommunityFeatureEnabled(config, appId);
  const label = app?.label || 'App';
  const themeColor = config?.primaryColor || config?.colors?.primary || '#1e4273';

  // Detect "iframe never loaded" — likely a framing block. We can't observe
  // onerror from cross-origin frames reliably, so use a 10s timeout as a
  // pragmatic signal that something went wrong.
  useEffect(() => {
    if (!enabled || loaded) return;
    const t = setTimeout(() => {
      if (!loaded) setErrored(true);
    }, 10000);
    return () => clearTimeout(t);
  }, [enabled, loaded]);

  // Reset load state when switching between embedded apps without unmounting
  // (React reuses this component across /community/library ↔ /community/housing
  // because both routes render the same element type).
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [appId, appUrl]);

  // Close semantics: the X button is a "dismiss this app context" gesture,
  // NOT a back button. We always navigate to home regardless of how the user
  // got here. Going back through history was confusing — if the iframe had
  // pushed any state OR the user deep-linked, navigate(-1) felt like a back
  // arrow rather than a close. navigate('/') is unambiguous.
  const handleClose = () => {
    navigate('/', { replace: true });
  };

  if (!enabled) {
    return (
      <div style={styles.notEnabled}>
        <h2>{label} is not enabled for this center.</h2>
        <button style={{ ...styles.primaryButton, background: themeColor }} onClick={() => navigate('/')}>Go home</button>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <header style={{ ...styles.header, background: themeColor }}>
        <span style={styles.title}>{label}</span>
        <div style={styles.headerActions}>
          {/* Escape hatch. Cheap insurance: if the target ever refuses framing
              we cannot detect it (see the docblock), and without this the pane
              is simply blank with no way forward. */}
          <a
            href={appUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${label} in a new tab`}
            title={`Open ${label} in a new tab`}
            style={styles.headerIconButton}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        <button
          type="button"
          aria-label={`Close ${label}`}
          onClick={handleClose}
          style={styles.closeButton}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        </div>
      </header>

      <div style={styles.iframeWrap}>
        {!loaded && !errored && (
          <div style={styles.loadingOverlay}>
            <div style={{ ...styles.spinner, borderTopColor: themeColor }} />
            <span style={styles.loadingText}>Loading {label}…</span>
          </div>
        )}

        {errored && (
          <div style={styles.errorOverlay}>
            <h3 style={{ margin: 0 }}>Couldn’t load {label} here.</h3>
            <p style={{ color: '#666', marginTop: 8, marginBottom: 16, textAlign: 'center' }}>
              It may not allow embedding. You can open it in a new tab instead.
            </p>
            <a
              href={appUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.primaryButton, background: themeColor, textDecoration: 'none' }}
            >
              Open in new tab
            </a>
            <button style={styles.secondaryButton} onClick={handleClose}>Go back</button>
          </div>
        )}

        <iframe
          // Keying by app id forces a fresh frame when the user moves between
          // two embedded apps, instead of reusing the previous app's document.
          key={appId}
          ref={iframeRef}
          src={appUrl}
          title={label}
          style={{ ...styles.iframe, opacity: loaded ? 1 : 0 }}
          onLoad={() => { setLoaded(true); setErrored(false); }}
          // Allow common features the embedded site may need; restrict the
          // most sensitive APIs by default. allow-same-origin keeps the
          // embedded app's own auth/storage working.
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox"
          // clipboard for copy buttons; fullscreen for embedded video players.
          allow="clipboard-read; clipboard-write; fullscreen"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  );
};

// Inline styles — kept here rather than CSS file to keep this self-contained.
// The page is full-viewport and overlays everything else, so global CSS isn't
// affected.
const HEADER_HEIGHT = 52;

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    zIndex: 1000,
  },
  header: {
    height: HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px 0 16px',
    color: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    flexShrink: 0,
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    letterSpacing: 0.2,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  headerIconButton: {
    width: 36,
    height: 40,
    borderRadius: 8,
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    transition: 'background 120ms ease',
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: 'none',
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 120ms ease',
  },
  iframeWrap: {
    flex: 1,
    position: 'relative',
    background: '#fafafa',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    transition: 'opacity 200ms ease',
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: '#fafafa',
    pointerEvents: 'none',
  },
  spinner: {
    width: 36,
    height: 36,
    border: '3px solid rgba(0,0,0,0.10)',
    borderRadius: '50%',
    animation: 'libspin 0.9s linear infinite',
  },
  loadingText: { color: '#777', fontSize: 14 },
  errorOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fafafa',
    padding: 24,
  },
  primaryButton: {
    color: '#fff',
    padding: '12px 18px',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
  },
  secondaryButton: {
    marginTop: 12,
    background: 'transparent',
    color: '#666',
    padding: '10px 14px',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
  },
  notEnabled: {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 24,
  },
};

// Inject the keyframes once (id-guarded so HMR / re-mounts don't duplicate).
if (typeof document !== 'undefined' && !document.getElementById('library-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'library-spin-keyframes';
  style.textContent = `@keyframes libspin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

export default EmbeddedAppPage;
